import { useEffect, useState, useCallback } from 'react';
/**
 * Returns layout metrics matching the CSS container query breakpoints.
 * These must stay in sync with @container rules in _react-player.scss.
 */
function getLayoutMetrics(containerWidth) {
    if (containerWidth < 320) {
        // Small: 34px btn, 0px gap, 30px slider, 2px padding each side
        return { buttonSlot: 34, volumeSliderWidth: 30, gap: 0, padding: 4 };
    }
    if (containerWidth < 480) {
        // Medium: 40px btn, 1px gap, 50px slider, 2px padding each side
        return { buttonSlot: 41, volumeSliderWidth: 50, gap: 1, padding: 4 };
    }
    // Default: 45px btn, 2px gap, 60px slider, 4px padding each side
    return { buttonSlot: 47, volumeSliderWidth: 60, gap: 2, padding: 8 };
}
/**
 * Tracks which collapsible buttons should overflow into the Actions menu
 * based on the available width of the controls container.
 *
 * Uses a callback ref so the ResizeObserver reconnects whenever the
 * controls-bottom div mounts/unmounts (e.g. when an error overlay hides
 * and then re-shows the control bar).
 *
 * @param collapsibleIds - Button IDs ordered by priority (highest first, stays visible longest)
 * @param isVideo - Whether the player is in video mode (affects mandatory width via fullscreen button)
 * @returns [callbackRef, overflowedSet] — attach the ref to the controls-bottom div
 */
export function useResponsiveControls(collapsibleIds, isVideo) {
    const [overflowed, setOverflowed] = useState(new Set());
    const [el, setEl] = useState(null);
    const callbackRef = useCallback((node) => {
        setEl(node);
    }, []);
    useEffect(() => {
        if (!el || typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver((entries) => {
            const containerWidth = entries[0]?.contentRect.width ?? 0;
            const m = getLayoutMetrics(containerWidth);
            // Mandatory elements that are always visible:
            // Left: PlayButton + gap + MuteButton + VolumeSlider
            const leftWidth = m.buttonSlot + m.gap + m.buttonSlot + m.volumeSliderWidth;
            // Right: ActionsMenu + (gap + FullscreenButton if video)
            const rightWidth = isVideo
                ? m.buttonSlot + m.gap + m.buttonSlot
                : m.buttonSlot;
            const mandatoryWidth = leftWidth + rightWidth + m.padding;
            const availableForCollapsible = containerWidth - mandatoryWidth;
            const maxVisible = Math.max(0, Math.floor(availableForCollapsible / m.buttonSlot));
            // collapsibleIds is ordered highest-priority first.
            // Show up to maxVisible from the front, overflow the rest.
            const newOverflowed = new Set();
            for (let i = maxVisible; i < collapsibleIds.length; i++) {
                newOverflowed.add(collapsibleIds[i]);
            }
            setOverflowed((prev) => {
                if (prev.size === newOverflowed.size &&
                    [...prev].every((id) => newOverflowed.has(id))) {
                    return prev;
                }
                return newOverflowed;
            });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [el, collapsibleIds, isVideo]);
    return [callbackRef, overflowed];
}
